import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ProviderGroupSearchParams } from 'src/app/shared/interfaces/provider/provider-group.interfaces';
import {
  IUserAccountDTO,
  IUserGroupNested,
} from 'src/app/shared/interfaces/user/user.interface';
import { ModalService } from 'src/app/shared/services/modal.service';
import { ProviderGroupGQLService } from 'src/app/shared/services/provider/provider-group.graphql.service';
import { UserStateService } from 'src/app/shared/services/users/user-state.service';

@Component({
  selector: 'add-group-to-user-grid',
  templateUrl: './add-group-to-user-grid.component.html',
})
export class AddGroupToUserGridComponent implements OnChanges {
  @Input() userGroups?: IUserGroupNested[];
  @Input() userAccounts?: IUserAccountDTO[];
  @Input() isFromModal = false;
  @Input() isUserEdit = false;
  @Input() profileMode = false;
  @Input() searchParams: ProviderGroupSearchParams;
  @Input() disabled = false;

  @Output() groupsCheck = new EventEmitter<IUserGroupNested[]>();
  @Output() groupsApplay = new EventEmitter<IUserGroupNested[]>();

  groups?: IUserGroupsCheckable[];
  checkedGroups?: IUserGroupsCheckable[];
  groupsCopy?: IUserGroupsCheckable[];

  nextPageCursor: string;
  emptyText: string;

  isGroupsSelected = false;
  isAllGroupsChacked = false;
  isGroupsLoading = false;

  constructor(
    private providerGroupGQLService: ProviderGroupGQLService,
    private modalService: ModalService,
    private userStateService: UserStateService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    const userGroups: IUserGroupsCheckable[] | undefined =
      changes.userGroups.currentValue;
    const userAccounts: IUserAccountDTO[] | undefined =
      changes.userAccounts.currentValue;
    const searchParams = changes.searchParams.currentValue;

    this.emptyText = this.profileMode
      ? 'Seems like some error occured.<br /> Conatct your adminstrator to add group(s) to this user.'
      : "You can add a group(s) by clicking the <i class='fas fa-plus-circle'></i> button above";

    if (userAccounts) {
      if (this.isUserEdit) {
        this.isUserEdit = false;
      } else {
        this.isGroupsSelected = false;
        this.groups = [];
        this.checkedGroups = [];
        this.getGroupsByAccounts(userAccounts);
      }
    }

    if (userGroups) {
      if (this.isFromModal) {
        this.checkedGroups = userGroups;
      } else {
        this.groupsCopy = userGroups;
        this.setGroups(userGroups);
      }

      if (this.profileMode) {
        this.setGroups(userGroups);
      }
    }

    if (searchParams) {
      this.groups = [];
      this.nextPageCursor = '';
      if (this.userAccounts) {
        this.getGroupsByAccounts(this.userAccounts);
      }
    }
  }

  toggleAllGroups(event?) {
    if (event) {
      this.isAllGroupsChacked = event.target.checked;
    }
    const condition = this.isAllGroupsChacked || false;

    if (condition) {
      const tmp = this.groups?.map((x) => {
        x.checked = this.isAllGroupsChacked;
        return x;
      });
      this.checkedGroups = this.isAllGroupsChacked ? tmp : [];
    }

    this.groupsCheck.emit(this.formatedGroups);
  }

  onGroupCheckboxChange(value: IUserGroupsCheckable) {
    this.checkedGroups = this.checkedGroups?.some((x) => {
      return x.groupId === value.groupId;
    })
      ? this.checkedGroups.filter((x) => {
          return x.groupId !== value.groupId;
        })
      : [...(this.checkedGroups ?? []), value];

    this.isAllGroupsChacked =
      this.checkedGroups.length === this.groups?.length &&
      this.checkedGroups.length > 0;

    this.groupsCheck.emit(this.formatedGroups);
  }

  onGroupSelected() {
    this.groupsCopy = this.groups;
    this.groups = this.checkedGroups;
    this.isGroupsSelected = true;
    this.groupsApplay.emit(this.formatedGroups);
  }

  async onGroupDelete(group: IUserGroupsCheckable) {
    const modalText = {
      bodyTitle: 'Are you sure you want to remove this group?',
      bodyText: 'This action will remove group from this user.',
    };

    await this.modalService.open('confirm', modalText).result;

    this.groups = this.groups?.filter((item) => {
      return item.groupId !== group.groupId;
    });

    if (this.groups?.length === 0) {
      this.isGroupsSelected = false;
      this.isAllGroupsChacked = false;

      this.groups = this.groupsCopy;
      this.checkedGroups = [];
    } else {
      this.checkedGroups = this.groups;
    }

    this.groupsCheck.emit(this.formatedGroups);
  }

  onScroll() {
    if (this.nextPageCursor && !this.isGroupsSelected) {
      if (this.userAccounts) {
        this.getGroupsByAccounts(this.userAccounts);
      }
    }
  }

  private getGroupsByAccounts(accounts: IUserAccountDTO[]) {
    console.log(accounts);

    const codes = accounts.map((x) => {
      return x.accountNumber;
    });

    const allowAllAccounts =
      this.userStateService.user?.accessLevel == 'SECURE_CONNECT' ||
      this.userStateService.user?.accessLevel == 'VENDOR';

    const allowedGroupsIds =
      allowAllAccounts ||
      (this.userStateService.user?.groups ?? []).length === 0
        ? undefined
        : this.userStateService.user?.groups?.map((x) => {
            return x.groupId;
          });

    this.isGroupsLoading = true;
    this.providerGroupGQLService
      .getProviderGroupsByAccounts({
        accountsCodes: codes,
        cursor: this.nextPageCursor,
        searchParams: this.searchParams,
        allowedGroupsIds,
      })
      .subscribe({
        next: (res) => {
          const page = res.pageInfo;
          const groups = res.nodes;

          this.nextPageCursor = page.hasNextPage ? page.endCursor : '';

          const formatedGroups = groups.map((group) => {
            return {
              ...group,
              checked: this.checkedGroups?.find((g) => {
                return g.groupId == group.groupId;
              })
                ? true
                : false,
            };
          });

          this.groups = this.groups ?? [];
          this.groups.push(...formatedGroups);

          this.groupsCopy = this.groups;

          this.isAllGroupsChacked =
            this.checkedGroups?.length === this.groups.length &&
            this.checkedGroups.length > 0;

          this.isGroupsLoading = false;
        },
        error: () => {
          this.isGroupsLoading = false;
        },
      });
  }

  private setGroups(groups: IUserGroupsCheckable[]) {
    this.groups = groups;
    this.checkedGroups = this.groups;
    this.isAllGroupsChacked = true;
    this.isGroupsSelected = true;
  }

  private get formatedGroups(): IUserGroupNested[] {
    return (
      this.checkedGroups?.map((group) => {
        return {
          groupId: group.groupId,
          name: group.name,
        };
      }) ?? []
    );
  }
}

export type IUserGroupsCheckable = {
  checked: boolean;
} & IUserGroupNested;
